import * as React from 'react';
import { REDUX_APP_SET_OVERLAY_OPEN } from 'redux-state/app/reducer.js';
import { useAppDispatch } from 'redux-state/configure-store.js';
import { getPageJson, PageJSONLoad } from 'routing/utils/json-fetch-page.js';
import { jsonMergeAdditional } from 'routing/utils/json-merge-additional.js';
import { RequestError } from 'utilities/fetch/api-gateway.js';
import { AppVersionContext } from 'utilities/hooks/context-app-version.js';
import { useNormalisedPathname } from 'utilities/router/api-fetch-path.js';
import { IConfigs } from '../../page/layout-build.js';
import { normaliseJsonModules } from './json-normalise.js';
import { UsePathResetHook } from './path-reset-hook.js';
import { trackPageView } from 'utilities/posthog-tracking';

type JsonResultLoading = {
  type: PageJSONLoad.Loading;
};

type JsonResultSuccess = {
  type: PageJSONLoad.Success;
} & IConfigs;

type JsonResultFail = {
  type: PageJSONLoad.Fail;
  error: RequestError;
};

type JsonResultReload = {
  type: PageJSONLoad.Reload;
};

export type JsonResultType = JsonResultLoading | JsonResultSuccess | JsonResultFail | JsonResultReload;

const getJsonPath = (pageJsonRef: JsonResultType) => {
  if (pageJsonRef.type === PageJSONLoad.Success) {
    return pageJsonRef.pageJson.url;
  }
  return Symbol();
};

/**
 * Called by cherry tree middleware whenever its routing changes
 * Gets new page json / payload based on new path and returns success / failure
 */
export const PageChangeUpdatePageJsonHook = () => {
  const path = useNormalisedPathname();
  UsePathResetHook({ path });
  const appDispatch = useAppDispatch();
  const { checkVersion } = React.useContext(AppVersionContext);

  const [_pageJson, setPageJson] = React.useState<JsonResultType>({
    type: PageJSONLoad.Loading,
  });
  const pageJsonRef = React.useRef(_pageJson);

  React.useEffect(() => {
    const buildPageJson = async (): Promise<JsonResultType> => {
      // getWindow()?.console.log('PAGE JSON PATH', path);
      try {
        if (!path) {
          return {
            type: PageJSONLoad.Loading,
          };
        }

        if (path === getJsonPath(pageJsonRef.current)) {
          /** Is request same of that on current page, we need routeStateRef as we dont need to reload page with overlay */
          return {
            type: PageJSONLoad.Reload,
          };
        }
        checkVersion();
        const pageJson = await getPageJson(path);
        const additionalJson = await jsonMergeAdditional(pageJson);
        const pageConfig = normaliseJsonModules(additionalJson);
        trackPageView(pageJson.location);
        if (pageConfig.type === 'overlay' && pageConfig.parent) {
          /** For use of displaying overlayLayout template below */
          appDispatch(REDUX_APP_SET_OVERLAY_OPEN());
          const overlayPageJson = await getPageJson(pageConfig.parent);
          const additionalJson = await jsonMergeAdditional(overlayPageJson);
          const pageConfigParent = await normaliseJsonModules(additionalJson);
          return { type: PageJSONLoad.Success, pageJson: pageConfigParent, overlay: pageConfig };
        }
        return { type: PageJSONLoad.Success, pageJson: pageConfig };
      } catch (_e) {
        // Request Error with either Network Error
        const error = _e as RequestError;
        return {
          type: PageJSONLoad.Fail,
          error,
        };
      }
    };

    const setNewPageJson = async () => {
      const newPageJson = await buildPageJson();
      setPageJson((c) => {
        /** Do not allow needless re-renders if pageJsonType is same as currently existing if is loading or in reload state */
        if (newPageJson.type === c.type && [PageJSONLoad.Loading, PageJSONLoad.Reload].includes(c.type)) {
          return c;
        }
        return newPageJson;
      });
    };
    setNewPageJson();
  }, [path, appDispatch, checkVersion]);

  React.useEffect(() => {
    pageJsonRef.current = _pageJson;
  }, [_pageJson]);

  const pageJson = React.useMemo(() => {
    return _pageJson;
  }, [_pageJson]);

  return pageJson;
};
